
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CompanyPerks, PerkRating } from '@/lib/types';
import PerkRatingField from './PerkRatingField';

interface CompanyPerksRatingProps {
  companyPerks: CompanyPerks | null;
  perkRatings: PerkRating[];
  onChange: (perkRatings: PerkRating[]) => void;
}

const CompanyPerksRating: React.FC<CompanyPerksRatingProps> = ({
  companyPerks,
  perkRatings,
  onChange
}) => {
  // Don't render anything if there are no perks
  if (!companyPerks) return null;
  
  // Transform perks object into an array of {id, text}
  const perksArray = [
    companyPerks.perk1 && { id: 'perk1', text: companyPerks.perk1 },
    companyPerks.perk2 && { id: 'perk2', text: companyPerks.perk2 },
    companyPerks.perk3 && { id: 'perk3', text: companyPerks.perk3 },
    companyPerks.perk4 && { id: 'perk4', text: companyPerks.perk4 },
    companyPerks.perk5 && { id: 'perk5', text: companyPerks.perk5 }
  ].filter(Boolean) as { id: string; text: string }[];
  
  // If after filtering we have no perks, don't render anything
  if (perksArray.length === 0) return null;

  const handlePerkRating = (perkId: string, perkText: string, isPositive: boolean | null, comment?: string) => {
    // Find if this perk already has a rating
    const existingRatingIndex = perkRatings.findIndex(rating => rating.perkId === perkId);
    
    // Create a copy of the current ratings
    const updatedRatings = [...perkRatings];
    
    if (existingRatingIndex >= 0) {
      if (isPositive === null && !comment) {
        // Remove the rating if it's being toggled off and has no comment
        updatedRatings.splice(existingRatingIndex, 1);
      } else {
        // Update existing rating
        updatedRatings[existingRatingIndex] = { 
          perkId, 
          perkText,
          isPositive,
          comment 
        };
      }
    } else if (isPositive !== null || comment) {
      // Add new rating if it doesn't exist and has either a rating or comment
      updatedRatings.push({ 
        perkId, 
        perkText,
        isPositive,
        comment 
      });
    }
    
    onChange(updatedRatings);
  };

  // Find the current rating and comment for each perk
  const getPerkRating = (perkId: string): boolean | null => {
    const rating = perkRatings.find(r => r.perkId === perkId);
    return rating ? rating.isPositive : null;
  };
  
  const getPerkComment = (perkId: string): string | undefined => {
    const rating = perkRatings.find(r => r.perkId === perkId);
    return rating?.comment;
  };

  return (
    <Card className="mt-6 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Rate Company Perks</CardTitle>
        <CardDescription>
          Let others know what you think about these company perks
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {perksArray.map(perk => (
            <PerkRatingField
              key={perk.id}
              perk={perk}
              value={getPerkRating(perk.id)}
              comment={getPerkComment(perk.id)}
              onChange={handlePerkRating}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyPerksRating;
